/**
 * Knowledge assembler — wires the independent knowledge sources together and
 * resolves the ordered fragment list for a research request. Each source stays
 * a standalone module; this file only composes references to them, so nothing
 * is a single giant object.
 */

import { BASE_KNOWLEDGE } from './base';
import { INDUSTRY_KNOWLEDGE } from './industry';
import { CATEGORY_KNOWLEDGE } from './category';
import { AUDIENCE_KNOWLEDGE } from './audience';
import { PURPOSE_KNOWLEDGE } from './purpose';
import { REGION_KNOWLEDGE } from './region';
import { CULTURE_KNOWLEDGE } from './culture';
import { SEASON_KNOWLEDGE } from './season';
import { EVENT_KNOWLEDGE } from './event';
import { BUSINESS_SIZE_KNOWLEDGE } from './businessSize';
import { PREMIUM_LEVEL_KNOWLEDGE } from './premiumLevel';
import { SOURCE_ORDER } from '../researchSchema';

/** Dimension → its keyed knowledge map (base is handled separately). */
const SOURCES = Object.freeze({
  industry: INDUSTRY_KNOWLEDGE,
  category: CATEGORY_KNOWLEDGE,
  businessSize: BUSINESS_SIZE_KNOWLEDGE,
  premiumLevel: PREMIUM_LEVEL_KNOWLEDGE,
  audience: AUDIENCE_KNOWLEDGE,
  purpose: PURPOSE_KNOWLEDGE,
  region: REGION_KNOWLEDGE,
  culture: CULTURE_KNOWLEDGE,
  season: SEASON_KNOWLEDGE,
  event: EVENT_KNOWLEDGE,
});

/**
 * Resolve the ordered fragments for a request (base-first → most specific).
 * @param {Object} request  Dimension → value.
 * @returns {{ fragments: Object[], labels: string[], matchedDims: string[] }}
 */
export const getOrderedFragments = (request = {}) => {
  const fragments = [BASE_KNOWLEDGE];
  const labels = ['base'];
  const matchedDims = [];

  SOURCE_ORDER.forEach((dim) => {
    if (dim === 'base') return;
    const value = request[dim];
    if (!value) return;
    const fragment = SOURCES[dim] && SOURCES[dim][value];
    if (fragment) {
      fragments.push(fragment);
      labels.push(`${dim}:${value}`);
      matchedDims.push(dim);
    }
  });

  return { fragments, labels, matchedDims };
};

/** Every authored fragment with a label — used for load-time validation. */
export const allFragments = () => {
  const list = [{ fragment: BASE_KNOWLEDGE, label: 'base' }];
  Object.entries(SOURCES).forEach(([dim, map]) => {
    Object.entries(map).forEach(([key, fragment]) => list.push({ fragment, label: `${dim}:${key}` }));
  });
  return list;
};

export { SOURCES };
